import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  runTransaction,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { useFirebase } from "../../Firebase/FirebaseContext";
import {
  CHAMP_ATTENTE,
  CHAMP_PLANIFIE,
  cheminStockageDirect,
  controlerDateRdv,
  correctionFile,
  etatFilePour,
  fileParJour,
  fusionnerFiles,
  jourLocal,
  jourPrevu,
  nomPatientFile,
  normaliserHeureRdv,
} from "../../Utils/fileModeDirect";
// Les MEMES controles que le pave d'identite du secretariat. Nicolas, 18/09
// 18h18 : « Les 2 cotes doivent avoir le meme fonctionnement. » Deux jeux de
// regles pour le meme champ, c'est deux verites sur le meme patient.
import { messageFormat, ponctuerDateNaissance } from "../../Utils/formatValeurs";

/**
 * Mode direct : la secrétaire prépare le patient, le préleveur le prend.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * LES QUATRE RETOURS DU 18/09
 * ─────────────────────────────────────────────────────────────────────────
 *
 * 1. « Retire le mot "File" dans "File Mode direct". Mode direct est
 *    suffisant. » — ici et dans le menu de gauche.
 *
 * 2. « Pour les champs à remplir, reprend le modèle de la tablette, là c'est
 *    pas propre et c'est écrit en anglais. » — les sept documents étaient des
 *    `<input type="file">` nus, que le navigateur rend en anglais
 *    (« Choose file / No file chosen ») et qu'aucune feuille de style ne
 *    touche. On les habille : un bouton par document, le nom du fichier une
 *    fois choisi, et de quoi le retirer.
 *
 * 3. « L'idée étant de gérer des patients avec RDV, rajoute un champ pour
 *    l'heure prévue de RDV qui sera affichée sur la tablette. »
 *
 * 4. « Il faut que l'on puisse modifier ou supprimer un patient qui serait
 *    dans la file. là une fois qu'il y est, c'est trop figé. » — la file
 *    n'était qu'une liste morte ; chaque ligne se modifie et se retire.
 *
 * CE QU'ON NE FAIT PAS : retirer de la file un patient DÉJÀ PRIS EN CHARGE.
 * Le bouton disparaît dès que le préleveur l'a ouvert sur sa tablette —
 * supprimer un dossier en cours de prélèvement effacerait un travail
 * commencé, et la secrétaire n'a aucun moyen de savoir, depuis cet écran, ce
 * qui a déjà été photographié.
 */

const SLOTS = [
  { key: "prescription", label: "Ordonnance" },
  { key: "prescriptiontwo", label: "Ordonnance 2" },
  { key: "mutual", label: "Mutuelle" },
  { key: "IDCard", label: "Pièce d'identité" },
  { key: "vitalCard", label: "Carte Vitale" },
  { key: "consent", label: "Consentement" },
  { key: "document", label: "Document" },
];

/*
  TOUS LES CHAMPS DE LA TABLETTE, ET DANS LE MEME ORDRE.

  Nicolas, 18/09 (18h18) : « Pour ajouter des patients dans la file du mode
  direct, reprends TOUS les champs du mode tablette standard avec notamment les
  verifications sur les telephones, mail... verification de la date de
  naissance. Les 2 cotes doivent avoir le meme fonctionnement. Egalement
  pouvoir ajouter un commentaire pour le preleveur. »

  Manquaient ici : les prenoms de naissance, le telephone, le courriel, le
  numero de dossier, et le commentaire. Un patient prepare au secretariat
  arrivait donc chez le preleveur moins complet qu'un patient saisi a
  l'accueil, et c'est le preleveur qui redemandait au patient ce que la
  secretaire avait deja sous les yeux.
*/
const FORMULAIRE_VIDE = {
  maritalName: "",
  lastName: "",
  name: "",
  firstNamesBirth: "",
  birthday: "",
  sex: "f",
  vital: "",
  phone: "",
  email: "",
  barcode: "",
  dateRdv: "",
  heureRdv: "",
  commentary: "",
};

// La date du rendez-vous vaut aujourd'hui tant que la secretaire n'en choisit
// pas une autre (Nicolas, 25/09 14h06) : calculee a chaque formulaire neuf,
// pas une fois au chargement de la page, qui peut rester ouverte la nuit.
const formulaireVide = () => ({ ...FORMULAIRE_VIDE, dateRdv: jourLocal() });

/**
 * Les champs qui portent une regle, et sous quelle cle `formatValeurs` la
 * connait. Un champ absent d'ici n'a pas de controle, et c'est voulu : un nom
 * de famille n'a pas de format.
 */
const CHAMPS_CONTROLES = [
  ["birthday", "dob"],
  ["vital", "nir"],
  ["phone", "phone"],
  ["email", "email"],
];

export default function FileModeDirect() {
  const { firebase, user } = useFirebase();
  const labId = localStorage.getItem("currentLab") || localStorage.getItem("laboratory");
  const [liste, setListe] = useState([]);
  const [form, setForm] = useState(formulaireVide);
  const [fichiers, setFichiers] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  // Le patient qu'on est en train de modifier, ou `null` pour un nouveau.
  const [enModification, setEnModification] = useState(null);
  const [aRetirer, setARetirer] = useState(null);

  // Deux lectures : les patients du jour (`enAttenteDirect`, ce que lit aussi
  // la tablette 3.2) et ceux d'un jour a venir (`attenteDirectPlanifiee`, que
  // la 3.2 ne lit pas). Voir Utils/fileModeDirect.js.
  useEffect(() => {
    if (!firebase?.db || !labId) return undefined;
    const parts = { [CHAMP_ATTENTE]: [], [CHAMP_PLANIFIE]: [] };
    const unsubs = [CHAMP_ATTENTE, CHAMP_PLANIFIE].map((champ) =>
      onSnapshot(
        query(
          collection(firebase.db, "appointements"),
          where("laboratoryId", "==", labId),
          where(champ, "==", true)
        ),
        (snap) => {
          parts[champ] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setListe(fusionnerFiles(parts[CHAMP_ATTENTE], parts[CHAMP_PLANIFIE]));
        },
        () => {
          setErreur("Index Firestore manquant ou lecture refusée.");
        }
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [firebase, labId]);

  // Le jour venu, un patient planifie redevient visible de la tablette 3.2 ;
  // un patient d'un jour a venir encore visible (ecrit avant le 25/09 17h18)
  // en est cache. C'est le travail de la tache de minuit du serveur : on le
  // refait ici pour qu'une tache manquee ne cache pas la journee. Relu dans
  // une transaction : un patient pris en charge entre-temps n'est pas remis
  // en file.
  const correctionsEnCours = useRef(new Set());
  useEffect(() => {
    if (!firebase?.db) return;
    const jour = jourLocal();
    for (const patient of liste) {
      if (!correctionFile(patient, jour) || correctionsEnCours.current.has(patient.id)) continue;
      correctionsEnCours.current.add(patient.id);
      const cible = doc(firebase.db, "appointements", patient.id);
      runTransaction(firebase.db, async (tx) => {
        const frais = await tx.get(cible);
        const c = frais.exists() ? correctionFile(frais.data(), jour) : null;
        if (c) tx.update(cible, c);
      })
        .catch((err) => console.warn("[mode direct] file du jour :", err?.message || err))
        .finally(() => correctionsEnCours.current.delete(patient.id));
    }
  }, [firebase, liste]);

  // Par jour (Nicolas, 25/09 14h06), puis par heure de rendez-vous, les
  // sans-heure a la fin : c'est l'ordre dans lequel la secretaire lit ses
  // journees. Un patient mis en file avant le 25/09 est du jour de sa mise en
  // file.
  const aujourdhui = jourLocal();
  const joursDeLaFile = useMemo(() => fileParJour(liste, aujourdhui), [liste, aujourdhui]);

  function reinitialiser() {
    setForm(formulaireVide());
    setFichiers({});
    setEnModification(null);
    setErreur("");
  }

  function modifier(patient) {
    setEnModification(patient);
    setForm({
      maritalName: patient.maritalName || "",
      lastName: patient.lastName || "",
      name: patient.name || "",
      firstNamesBirth: patient.firstNamesBirth || "",
      birthday: patient.birthday || "",
      sex: patient.sex || "f",
      vital: patient.vital || "",
      phone: patient.phone || "",
      email: patient.email || "",
      barcode: patient.barcode || "",
      dateRdv: jourPrevu(patient),
      heureRdv: patient.heureRdv || "",
      commentary: patient.commentary || "",
    });
    setFichiers({});
    setErreur("");
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  }

  /**
   * Ouvrir un document deja joint, dans un onglet.
   *
   * Nicolas, 19/09 : « quand un patient est dans la liste et que l'on
   * souhaite le modifier nous n'avons pas acces aux documents ajoutes, ce
   * serait bien qu'on puisse le regarder et eventuellement un Reset. »
   *
   * La secretaire ne pouvait que REMPLACER a l'aveugle : impossible de savoir
   * si l'ordonnance deja jointe etait la bonne, ni meme lisible.
   */
  async function voirDocument(chemin) {
    if (!chemin) return;
    // L'onglet s'ouvre AVANT l'appel reseau, tant qu'on est dans le clic :
    // un `window.open` apres un `await` est bloque par Chrome.
    const onglet = window.open("", "_blank");
    try {
      const url = await getDownloadURL(ref(getStorage(), chemin));
      if (onglet) onglet.location = url;
      else window.open(url, "_blank");
    } catch (err) {
      onglet?.close();
      setErreur("Ce document n'a pas pu être ouvert.");
    }
  }

  /**
   * Retirer un document du dossier (le « Reset » qu'il demande).
   *
   * On efface la REFERENCE sur le rendez-vous, pas le fichier dans le bucket :
   * une purge est irreversible, et une secretaire qui se trompe de bouton ne
   * doit pas detruire l'ordonnance d'un patient. Le fichier orphelin part avec
   * la regle de cycle de vie du stockage.
   */
  async function retirerDuDossier(cle) {
    if (!enModification?.id || !enModification?.[cle]) return;
    try {
      await updateDoc(doc(firebase.db, "appointements", enModification.id), { [cle]: null });
      setEnModification((p) => ({ ...p, [cle]: null }));
    } catch (err) {
      setErreur(err.message || "Retrait refusé");
    }
  }

  async function retirer(patient) {
    try {
      await deleteDoc(doc(firebase.db, "appointements", patient.id));
      setARetirer(null);
      if (enModification?.id === patient.id) reinitialiser();
    } catch (err) {
      setErreur(err.message || "Suppression refusée");
      setARetirer(null);
    }
  }

  async function envoyer(e) {
    e.preventDefault();
    if (!form.lastName && !form.maritalName) {
      setErreur("Nom obligatoire");
      return;
    }
    const heure = normaliserHeureRdv(form.heureRdv);
    if (form.heureRdv && !heure) {
      setErreur("L'heure de rendez-vous doit s'écrire HH:MM (par exemple 08:30).");
      return;
    }
    // En modification, la date deja enregistree (meme passee) reste permise :
    // corriger un nom ne doit pas obliger a deplacer le rendez-vous.
    const dateControlee = controlerDateRdv(form.dateRdv, {
      dateEnregistree: enModification ? jourPrevu(enModification) : null,
    });
    if (dateControlee.erreur) {
      setErreur(dateControlee.erreur);
      return;
    }
    /*
      ON REFUSE D'ENFILER UN PATIENT DONT UN CHAMP EST HORS FORMAT.

      Les memes regles que le pave d'identite du secretariat (`messageFormat`),
      pas une deuxieme copie : un numero de securite sociale dont la cle est
      fausse doit etre faux des deux cotes de l'ecran, sinon on ne sait plus
      lequel croire. Un champ VIDE ne declenche rien -- la secretaire n'a pas
      toujours le telephone du patient, et ce n'est pas une faute.
    */
    const horsFormat = CHAMPS_CONTROLES.map(([cle, champ]) => {
      const valeur = String(form[cle] || "").trim();
      if (!valeur) return null;
      const message = messageFormat(champ, valeur);
      return message ? message : null;
    }).filter(Boolean);
    if (horsFormat.length) {
      setErreur(horsFormat.join(" "));
      return;
    }
    setEnvoi(true);
    setErreur("");
    try {
      // Seuls les documents CHOISIS cette fois-ci sont televerses : modifier
      // un patient ne doit pas effacer les pieces deja en place.
      const docs = {};
      const storage = getStorage();
      for (const slot of SLOTS) {
        const f = fichiers[slot.key];
        if (!f) continue;
        const chemin = cheminStockageDirect(slot.key, f.name);
        await uploadBytes(ref(storage, chemin), f);
        docs[slot.key] = chemin;
      }

      const champs = {
        name: form.name.trim(),
        lastName: String(form.lastName || "").toUpperCase().trim(),
        maritalName: String(form.maritalName || "").toUpperCase().trim() || null,
        // Les prenoms de naissance au complet : le back-office et les PDF les
        // attendent sous ce nom depuis le 02/09.
        firstNamesBirth: String(form.firstNamesBirth || "").trim() || form.name.trim() || null,
        birthday: form.birthday.trim(),
        sex: form.sex,
        vital: form.vital.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        barcode: form.barcode.trim() || null,
        // « AAAA-MM-JJ », date locale du laboratoire (25/09 14h06). La
        // tablette 3.2 ne la lit pas : un patient d'un jour a venir lui est
        // cache par `attenteDirectPlanifiee` (plus bas) ; la suivante
        // n'affiche que le jour J et deroule les autres jours.
        dateRdv: dateControlee.date,
        heureRdv: heure || null,
        // Le mot laisse au preleveur (« Egalement pouvoir ajouter un
        // commentaire pour le preleveur », 18/09).
        commentary: form.commentary.trim() || null,
        ...docs,
      };

      // Un patient d'un jour a venir est « planifie » : la tablette 3.2 ne le
      // voit pas avant son jour (Utils/fileModeDirect.js).
      const etat = etatFilePour(dateControlee.date);
      if (enModification) {
        // Changer la date deplace le patient entre « du jour » et « planifie »,
        // mais seulement s'il est encore en file : un patient que le preleveur
        // a pris entre-temps n'y revient pas.
        const cible = doc(firebase.db, "appointements", enModification.id);
        await runTransaction(firebase.db, async (tx) => {
          const frais = await tx.get(cible);
          const d = frais.exists() ? frais.data() : {};
          const enFile = d[CHAMP_ATTENTE] === true || d[CHAMP_PLANIFIE] === true;
          tx.update(cible, enFile ? { ...champs, ...etat } : champs);
        });
      } else {
        await addDoc(collection(firebase.db, "appointements"), {
          ...champs,
          laboratoryId: labId,
          modeDirect: true,
          ...etat,
          created_at: serverTimestamp(),
          created_by: user?.email || "",
        });
      }
      reinitialiser();
    } catch (err) {
      setErreur(err.message || "Enregistrement refusé");
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
      <h2 style={{ marginBottom: 4 }}>Mode direct</h2>
      <p style={{ color: "#64748b", fontSize: 14, marginTop: 0 }}>
        Préparez les patients (rendez-vous, domicile). Le préleveur les prend
        dans sa liste Mode direct, avec les documents.
      </p>

      <form onSubmit={envoyer} style={carte}>
        {enModification ? (
          <div style={bandeauModif}>
            Modification de <strong>{nomPatientFile(enModification)}</strong>
            <button type="button" onClick={reinitialiser} style={lienBouton}>
              Annuler
            </button>
          </div>
        ) : null}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={etiquette}>
            Nom d'usage
            <input
              value={form.maritalName}
              onChange={(e) => setForm({ ...form, maritalName: e.target.value.toUpperCase() })}
              style={champ}
            />
          </label>
          <label style={etiquette}>
            Nom de naissance
            <input
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value.toUpperCase() })}
              style={champ}
            />
          </label>
          <label style={etiquette}>
            Prénom
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={champ}
            />
          </label>
          <label style={etiquette}>
            Prénoms de naissance
            <input
              value={form.firstNamesBirth}
              onChange={(e) => setForm({ ...form, firstNamesBirth: e.target.value })}
              placeholder="Tous les prénoms, dans l'ordre"
              style={champ}
            />
          </label>
          <Champ
            libelle="Date de naissance"
            valeur={form.birthday}
            // Les barres obliques se posent toutes seules (19/09) : personne
            // ne les tape, et les reclamer faisait corriger a la main ce que
            // la machine sait ecrire.
            onChange={(v) => setForm({ ...form, birthday: ponctuerDateNaissance(v) })}
            placeholder="JJ/MM/AAAA"
            controle="dob"
          />
          <label style={etiquette}>
            Sexe
            <select
              value={form.sex}
              onChange={(e) => setForm({ ...form, sex: e.target.value })}
              style={champ}
            >
              <option value="f">Féminin</option>
              <option value="h">Masculin</option>
            </select>
          </label>
          <Champ
            libelle="Numéro de sécurité sociale"
            valeur={form.vital}
            onChange={(v) => setForm({ ...form, vital: v })}
            placeholder="15 chiffres"
            controle="nir"
          />
          <Champ
            libelle="Téléphone"
            valeur={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
            placeholder="06 12 34 56 78"
            controle="phone"
          />
          <Champ
            libelle="Adresse e-mail"
            valeur={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            placeholder="patient@exemple.fr"
            controle="email"
          />
          <Champ
            libelle="N° de dossier"
            valeur={form.barcode}
            onChange={(v) => setForm({ ...form, barcode: v })}
            placeholder="Code-barres du dossier"
          />
          {/* Nicolas, 18/09 : l'heure prevue, affichee sur la tablette ; le
              25/09 14h06, la date aussi (aujourd'hui par defaut). */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <label style={etiquette}>
              Date du rendez-vous
              <input
                type="date"
                value={form.dateRdv}
                min={enModification ? undefined : aujourdhui}
                onChange={(e) => setForm({ ...form, dateRdv: e.target.value })}
                style={champ}
              />
            </label>
            <label style={etiquette}>
              Heure
              <input
                type="time"
                value={form.heureRdv}
                onChange={(e) => setForm({ ...form, heureRdv: e.target.value })}
                style={champ}
              />
            </label>
          </div>
        </div>

        {/* Le mot laissé au préleveur (18/09). Il le lit avant de prendre le
            patient : « allergique au latex », « vient avec sa fille »… */}
        <label style={{ ...etiquette, display: "block", marginTop: 12 }}>
          Commentaire pour le préleveur
          <textarea
            value={form.commentary}
            onChange={(e) => setForm({ ...form, commentary: e.target.value })}
            rows={2}
            placeholder="Ce que le préleveur doit savoir avant de prendre ce patient"
            style={{ ...champ, resize: "vertical", minHeight: 52 }}
          />
        </label>

        <div style={{ marginTop: 18 }}>
          <div style={titreSection}>Documents</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {SLOTS.map((s) => (
              <ChoixFichier
                key={s.key}
                libelle={s.label}
                fichier={fichiers[s.key]}
                cheminAuDossier={enModification?.[s.key] || null}
                onVoir={() => voirDocument(enModification?.[s.key])}
                onReset={() => retirerDuDossier(s.key)}
                onChoisir={(f) => setFichiers({ ...fichiers, [s.key]: f })}
                onRetirer={() => {
                  const suite = { ...fichiers };
                  delete suite[s.key];
                  setFichiers(suite);
                }}
              />
            ))}
          </div>
        </div>

        {erreur ? <div style={messageErreur}>{erreur}</div> : null}

        <button type="submit" disabled={envoi} style={boutonPrincipal}>
          {envoi
            ? "Enregistrement…"
            : enModification
            ? "Enregistrer les modifications"
            : "Mettre en file"}
        </button>
      </form>

      <h3 style={{ marginBottom: 8 }}>En attente ({liste.length})</h3>
      {liste.length === 0 ? (
        <p style={{ color: "#64748b" }}>Personne en file.</p>
      ) : (
        joursDeLaFile.map((groupe) => (
          <section key={groupe.jour} style={{ marginBottom: 16 }}>
            <div style={{ ...titreJour, ...(groupe.passe ? titreJourPasse : null) }}>
              {groupe.libelle} · {groupe.patients.length}
              {groupe.passe ? " · jour passé, pas encore pris en charge" : ""}
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {groupe.patients.map((p) => (
                <li key={p.id} style={ligneFile}>
                  <div style={{ minWidth: 60 }}>
                    {p.heureRdv ? (
                      <span style={pastilleHeure}>{p.heureRdv}</span>
                    ) : (
                      <span style={{ ...pastilleHeure, background: "#f1f5f9", color: "#64748b" }}>
                        sans RDV
                      </span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong>{nomPatientFile(p)}</strong>
                    {p.birthday ? ` · ${p.birthday}` : ""}
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      {SLOTS.filter((s) => p[s.key]).map((s) => s.label).join(" · ") ||
                        "Aucun document"}
                    </div>
                    {p.commentary ? (
                      <div style={{ fontSize: 12, color: "#075985", marginTop: 2 }}>
                        « {p.commentary} »
                      </div>
                    ) : null}
                  </div>
                  {aRetirer === p.id ? (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: "#9f1239" }}>Retirer de la file ?</span>
                      <button type="button" onClick={() => retirer(p)} style={boutonDanger}>
                        Oui, retirer
                      </button>
                      <button type="button" onClick={() => setARetirer(null)} style={boutonSecondaire}>
                        Non
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 6 }}>
                      <button type="button" onClick={() => modifier(p)} style={boutonSecondaire}>
                        Modifier
                      </button>
                      <button type="button" onClick={() => setARetirer(p.id)} style={boutonSecondaire}>
                        Retirer
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

/**
 * Un champ de saisie qui DIT ce qui ne va pas, sous lui, pendant la frappe.
 *
 * Le message vient de `Utils/formatValeurs`, la meme regle que le pave
 * d'identite du secretariat : « Les 2 cotes doivent avoir le meme
 * fonctionnement » (Nicolas, 18/09 18h18). Il n'empeche pas de taper — on
 * signale, on ne bloque pas la secretaire au milieu d'une file d'attente —
 * mais `envoyer` refuse tant qu'il reste quelque chose a corriger.
 */
function Champ({ libelle, valeur, onChange, placeholder, controle }) {
  const message = controle ? messageFormat(controle, valeur) : "";
  const enDefaut = !!(valeur || "").trim() && !!message;
  return (
    <label style={etiquette}>
      {libelle}
      <input
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={enDefaut ? { ...champ, borderColor: "#fca5a5", background: "#fff7f7" } : champ}
      />
      {enDefaut ? (
        <span style={{ display: "block", marginTop: 3, fontSize: 11, color: "#9f1239", fontWeight: 500 }}>
          {message}
        </span>
      ) : null}
    </label>
  );
}

/**
 * Un document a joindre.
 *
 * `<input type="file">` est rendu par le navigateur, dans SA langue et avec
 * SON apparence : « Choose file / No file chosen » sur un poste en anglais,
 * et aucune feuille de style ne l'atteint. On le cache donc derrière un
 * bouton à nous — c'est la seule façon d'avoir un libellé français et une
 * apparence cohérente avec le reste de l'écran.
 */
function ChoixFichier({ libelle, fichier, cheminAuDossier, onChoisir, onRetirer, onVoir, onReset }) {
  const id = `fichier-${libelle.replace(/\s+/g, "-")}`;
  return (
    <div style={carteFichier}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{libelle}</div>
      <input
        id={id}
        type="file"
        accept="image/*,.pdf"
        style={{ display: "none" }}
        onChange={(e) => onChoisir(e.target.files?.[0])}
      />
      {fichier ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span style={nomFichier} title={fichier.name}>
            {fichier.name}
          </span>
          <button type="button" onClick={onRetirer} style={lienBouton}>
            Retirer
          </button>
        </div>
      ) : (
        <label htmlFor={id} style={boutonFichier}>
          {cheminAuDossier ? "Remplacer le fichier" : "Choisir un fichier"}
        </label>
      )}
      {/* Le document deja joint se REGARDE et se retire (19/09) : on ne
          remplace plus a l'aveugle. */}
      {cheminAuDossier && !fichier ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: "#0b6f3b", fontWeight: 600 }}>Déjà au dossier</span>
          <button type="button" onClick={onVoir} style={lienBouton}>
            Voir
          </button>
          <button type="button" onClick={onReset} style={{ ...lienBouton, color: "#9f1239" }}>
            Reset
          </button>
        </div>
      ) : null}
    </div>
  );
}

const carte = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
  marginBottom: 24,
};
const champ = {
  display: "block",
  width: "100%",
  marginTop: 4,
  padding: 8,
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  boxSizing: "border-box",
  fontSize: 14,
  fontFamily: "inherit",
};
const etiquette = { fontSize: 13, fontWeight: 600, color: "#334155" };
const titreSection = {
  fontSize: 12,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  color: "#64748b",
  marginBottom: 8,
};
const carteFichier = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
};
const boutonFichier = {
  display: "inline-block",
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  width: "fit-content",
};
const nomFichier = {
  fontSize: 12,
  color: "#0b6f3b",
  fontWeight: 600,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const boutonPrincipal = {
  marginTop: 16,
  padding: "9px 16px",
  borderRadius: 8,
  background: "#0596DE",
  color: "#fff",
  border: 0,
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
};
const boutonSecondaire = {
  padding: "5px 10px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};
const boutonDanger = {
  padding: "5px 10px",
  borderRadius: 8,
  border: "1px solid #fecdd3",
  background: "#fff1f2",
  color: "#9f1239",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
};
const lienBouton = {
  border: 0,
  background: "none",
  color: "#0596DE",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  padding: 0,
};
const ligneFile = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 0",
  borderBottom: "1px solid #e2e8f0",
};
const pastilleHeure = {
  display: "inline-block",
  padding: "3px 8px",
  borderRadius: 999,
  background: "#e0f2fe",
  color: "#075985",
  fontSize: 12,
  fontWeight: 800,
  fontFamily: "monospace",
};
const titreJour = {
  fontSize: 12,
  fontWeight: 700,
  color: "#334155",
  padding: "6px 0",
  borderBottom: "1px solid #e2e8f0",
};
const titreJourPasse = { color: "#9a3412" };
const bandeauModif = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 10px",
  borderRadius: 8,
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
  color: "#1e40af",
  fontSize: 13,
  marginBottom: 12,
};
const messageErreur = {
  marginTop: 10,
  padding: "8px 10px",
  borderRadius: 8,
  background: "#fff1f2",
  border: "1px solid #fecdd3",
  color: "#9f1239",
  fontSize: 13,
};
